import { NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    input,
    OnDestroy,
    signal,
    viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { LucideChevronRight, LucideDynamicIcon, LucideEllipsis } from '@lucide/angular';
import { ButtonDirective } from 'primeng/button';
import { Popover } from 'primeng/popover';
import { Tooltip } from 'primeng/tooltip';

import { MenuItemAdvania } from '../menu/menu-utils';
import { toBreadcrumbSegments } from './breadcrumb.utils';

@Component({
    selector: 'adv-breadcrumb',
    imports: [RouterLink, Tooltip, Popover, NgTemplateOutlet, ButtonDirective, LucideDynamicIcon],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './breadcrumb.component.html',
})
export class BreadcrumbComponent implements AfterViewInit, OnDestroy {
    home = input<MenuItemAdvania>();
    currentUrl = input('');

    private _breadcrumbContainer = viewChild<ElementRef<HTMLElement>>('breadcrumbContainer');
    private _fullBreadcrumb = viewChild<ElementRef<HTMLElement>>('fullBreadcrumb');

    private _needsCollapse = signal(false);
    private _resizeObserver?: ResizeObserver;
    private _windowResizeHandler?: () => void;

    protected readonly ChevronRight = LucideChevronRight;
    protected readonly MoreHorizontal = LucideEllipsis;
    protected readonly segments = computed<MenuItemAdvania[]>(() => {
        return toBreadcrumbSegments(this.currentUrl());
    });

    // Determines which segments to display based on available space
    protected visibleSegments = computed(() => {
        const allSegments = this.segments();
        const shouldCollapse = this._needsCollapse();
        const isNarrow = this.isNarrowContainer();

        // If it fits, show all
        if (!shouldCollapse) {
            return allSegments;
        }

        // Doesn't fit: narrow shows last only, wide shows first + last
        if (isNarrow) {
            return [allSegments[allSegments.length - 1]];
        }
        return [allSegments[0], allSegments[allSegments.length - 1]];
    });

    // Segments hidden in popover menu
    protected hiddenSegments = computed(() => {
        const allSegments = this.segments();
        const shouldCollapse = this._needsCollapse();
        const isNarrow = this.isNarrowContainer();

        // If it fits, nothing to hide
        if (!shouldCollapse) {
            return [];
        }

        // Doesn't fit: narrow hides all except last, wide hides middle segments
        if (isNarrow) {
            return allSegments.slice(0, -1);
        }
        return allSegments.slice(1, -1);
    });

    protected showCollapseButton = computed(() => {
        return this._needsCollapse() && this.hiddenSegments().length > 0;
    });

    protected isNarrowContainer = signal(false);

    constructor() {
        effect(() => {
            this.segments();
            this.home();
            // Re-measure after DOM updates
            setTimeout(() => this.checkIfShouldCollapse(), 0);
        });
    }

    ngAfterViewInit() {
        this.checkIfShouldCollapse();
        this.setupResizeObserver();
        this.setupWindowResizeListener();
    }

    private setupResizeObserver() {
        const container = this._breadcrumbContainer()?.nativeElement;
        if (!container) {
            return;
        }

        this._resizeObserver = new ResizeObserver(() => {
            this.checkIfShouldCollapse();
        });

        this._resizeObserver.observe(container);
    }

    private setupWindowResizeListener() {
        this._windowResizeHandler = () => {
            this.checkIfShouldCollapse();
        };

        window.addEventListener('resize', this._windowResizeHandler);
    }

    ngOnDestroy() {
        this._resizeObserver?.disconnect();
        if (this._windowResizeHandler) {
            window.removeEventListener('resize', this._windowResizeHandler);
        }
    }

    /**
     * Measures container vs full breadcrumb width to determine collapse state.
     * Tracks narrow containers (< 768px) for mobile-optimized layout.
     */
    private checkIfShouldCollapse(): void {
        const container = this._breadcrumbContainer()?.nativeElement;
        const fullBreadcrumb = this._fullBreadcrumb()?.nativeElement;

        if (!container || !fullBreadcrumb) {
            return;
        }

        const containerWidth = container.offsetWidth;
        const fullBreadcrumbWidth = fullBreadcrumb.scrollWidth;

        const isNarrow = containerWidth < 768;
        if (this.isNarrowContainer() !== isNarrow) {
            this.isNarrowContainer.set(isNarrow);
        }

        const shouldCollapse = fullBreadcrumbWidth > containerWidth;
        if (this._needsCollapse() !== shouldCollapse) {
            this._needsCollapse.set(shouldCollapse);
        }
    }
}
